import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2,
  Search,
  Edit,
  Trash2,
  Users,
  Calendar,
  Mail,
  Eye,
  Loader2,
  Crown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  MoreVertical,
  Globe,
  Phone,
  Briefcase,
  UserCog,
  Pause,
  Play,
  DollarSign,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { ADMIN_BASE_URL } from '../config/admin';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MSPManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [msps, setMsps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Dialog states
  const [selectedMsp, setSelectedMsp] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Edit form
  const [editForm, setEditForm] = useState({
    company_name: '',
    website: '',
    phone: '',
    status: 'active',
    commission_rate: 0.30,
  });

  useEffect(() => {
    fetchMsps();
  }, []);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const fetchMsps = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/msps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMsps(response.data.msps || []);
    } catch (error) {
      toast.error('Failed to fetch MSPs');
    } finally {
      setLoading(false);
    }
  };

  const handleEditMsp = async () => {
    if (!selectedMsp) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/admin/msps/${selectedMsp.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('MSP updated successfully');
      setShowEditDialog(false);
      fetchMsps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update MSP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuspendMsp = async () => {
    if (!selectedMsp) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const isSuspended = selectedMsp.status !== 'suspended';
      await axios.put(`${API}/api/admin/msps/${selectedMsp.id}/suspend`, 
        { is_suspended: isSuspended },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`MSP ${isSuspended ? 'suspended' : 'reactivated'} successfully`);
      setShowSuspendDialog(false);
      fetchMsps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update MSP status');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMsp = async () => {
    if (!selectedMsp) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/msps/${selectedMsp.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('MSP deleted successfully');
      setShowDeleteDialog(false);
      fetchMsps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete MSP');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (msp) => {
    setSelectedMsp(msp);
    setEditForm({
      company_name: msp.company_name || '',
      website: msp.website || '',
      phone: msp.phone || '',
      status: msp.status || 'active',
      commission_rate: msp.commission_rate || 0.30,
    });
    setShowEditDialog(true);
  };

  const filteredMsps = msps.filter(msp => {
    const matchesSearch = 
      (msp.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (msp.contact_email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || msp.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const totalItems = filteredMsps.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMsps = filteredMsps.slice(startIndex, endIndex);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
      case 'suspended':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />Suspended</Badge>;
      case 'pending':
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30"><AlertTriangle className="w-3 h-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="outline" className="border-slate-600">{status}</Badge>;
    }
  };

  const stats = {
    total: msps.length,
    active: msps.filter(m => m.status === 'active').length,
    suspended: msps.filter(m => m.status === 'suspended').length,
    totalClients: msps.reduce((sum, m) => sum + (m.client_count || 0), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">MSP Management</h1>
        <p className="text-slate-400 mt-1">Manage Managed Service Providers and their operations</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Building2 className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-slate-400">Total MSPs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.active}</p>
                <p className="text-xs text-slate-400">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.suspended}</p>
                <p className="text-xs text-slate-400">Suspended</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Briefcase className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.totalClients}</p>
                <p className="text-xs text-slate-400">Total Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Pagination Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search by company name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-700"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-slate-900 border-slate-700">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-400">Show:</span>
          <Select value={itemsPerPage.toString()} onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
            <SelectTrigger className="w-[80px] bg-slate-900 border-slate-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* MSP Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {paginatedMsps.map((msp) => (
          <motion.div
            key={msp.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                      {(msp.company_name || 'M')[0].toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{msp.company_name || 'Unnamed MSP'}</h3>
                      <p className="text-sm text-slate-400">{msp.contact_email}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
                      <DropdownMenuItem 
                        className="cursor-pointer"
                        onClick={() => navigate(`${ADMIN_BASE_URL}/msps/${msp.id}`)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="cursor-pointer"
                        onClick={() => openEditDialog(msp)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-slate-700" />
                      <DropdownMenuItem 
                        className="cursor-pointer"
                        onClick={() => {
                          setSelectedMsp(msp);
                          setShowSuspendDialog(true);
                        }}
                      >
                        {msp.status === 'suspended' ? (
                          <>
                            <Play className="w-4 h-4 mr-2 text-emerald-400" />
                            <span className="text-emerald-400">Reactivate</span>
                          </>
                        ) : (
                          <>
                            <Pause className="w-4 h-4 mr-2 text-amber-400" />
                            <span className="text-amber-400">Suspend</span>
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="cursor-pointer text-red-400 focus:text-red-400"
                        onClick={() => {
                          setSelectedMsp(msp);
                          setShowDeleteDialog(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400 text-sm">Status</span>
                    {getStatusBadge(msp.status)}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400 text-sm">Commission Rate</span>
                    <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30">
                      {Math.round((msp.commission_rate || 0.30) * 100)}%
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-400">{msp.client_count || 0} clients</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-400">{msp.team_count || 0} team</span>
                    </div>
                  </div>

                  {msp.website && (
                    <div className="flex items-center gap-2 text-sm">
                      <Globe className="w-4 h-4 text-slate-500" />
                      <a href={msp.website} target="_blank" rel="noopener noreferrer" 
                         className="text-blue-400 hover:underline truncate">
                        {msp.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-400">
                      Joined {new Date(msp.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 mt-4 pt-4 border-t border-slate-800">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => navigate(`${ADMIN_BASE_URL}/msps/${msp.id}`)}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => openEditDialog(msp)}
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {filteredMsps.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No MSPs Found</h3>
          <p className="text-slate-400">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'No Managed Service Providers registered yet'}
          </p>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-slate-800">
          <p className="text-sm text-slate-400">
            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} MSPs
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="border-slate-700"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className={currentPage === pageNum ? "bg-blue-600" : "border-slate-700"}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="border-slate-700"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Edit MSP</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update MSP information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={editForm.company_name}
                onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <Input
                value={editForm.website}
                onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                placeholder="https://example.com"
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="+1 (555) 000-0000"
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-green-400" />
                Commission Rate
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={Math.round(editForm.commission_rate * 100)}
                  onChange={(e) => setEditForm({ ...editForm, commission_rate: parseFloat(e.target.value) / 100 })}
                  className="bg-slate-800 border-slate-700 w-24"
                />
                <span className="text-slate-400">%</span>
                <span className="text-xs text-slate-500">(Default: 30%)</span>
              </div>
              <p className="text-xs text-slate-500">
                Commission earned on all client subscriptions managed by this MSP
              </p>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={editForm.status} 
                onValueChange={(value) => setEditForm({ ...editForm, status: value })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditMsp} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">
              {selectedMsp?.status === 'suspended' ? 'Reactivate' : 'Suspend'} MSP
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedMsp?.status === 'suspended' 
                ? `Are you sure you want to reactivate "${selectedMsp?.company_name}"? This will restore access for all their team members.`
                : `Are you sure you want to suspend "${selectedMsp?.company_name}"? This will prevent all their team members from accessing the platform.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuspendDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSuspendMsp} 
              disabled={submitting}
              className={selectedMsp?.status === 'suspended' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {selectedMsp?.status === 'suspended' ? 'Reactivate' : 'Suspend'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete MSP
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete <strong>&quot;{selectedMsp?.company_name}&quot;</strong>?
              <br /><br />
              <span className="text-red-400 font-medium">This action cannot be undone.</span> This will permanently delete:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>The MSP account and all settings</li>
                <li>All {selectedMsp?.team_count || 0} team members</li>
                <li>All {selectedMsp?.client_count || 0} client companies and their data</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteMsp} 
              disabled={submitting}
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete MSP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
